<script lang="ts">
    import { onMount } from 'svelte'
    import Content from '../_common/content.svelte'
    import LoadSpinner from '../_common/load-spinner.svelte'
    import DateSelect from '../_common/date-select.svelte'
    import ZentitySelect from '../_common/z/zentity-select.svelte'
    import Zact from '../_common/z/zact.svelte'
    import Zgrid from '../_common/z/zgrid.svelte'
    import Zinput from '../_common/z/zinput.svelte'
    import { GridBadge, GridClass, GridCol, GridColTypeEnum, GridLine } from '../_common/z/zgrid'
    import type { Service } from '../../modules/posApi/services/service'
    import type { ProductOpenUnit } from '../../modules/posApi/stock/product-consumption/product-open-unit'
    import type { ProductConsumptionLog } from '../../modules/posApi/stock/product-consumption/product-consumption-log'
    import type { ProductMovement } from '../../modules/posApi/stock/product-movements/product-movement'

    type Action = 'Abrindo' | 'Usando' | 'Finalizando'
    type Row = {
        name: string
        action: Action
        when: Date
        detail: string
        userId?: string
        loggedUserId?: string
        user?: { userName: string }
        loggedUser?: { userName: string }
    }

    let show = false
    let dates: { from: Date; to: Date } = window.api.utils.dates.currentMonth()
    let dateSelect
    let selectedService: Service = null

    const exibirOptions = [
        { value: null, label: 'Todos' },
        { value: 'open', label: 'Insumos abertos' },
        { value: 'using', label: 'Insumos em uso' },
        { value: 'closed', label: 'Insumos finalizados' },
    ]
    let exibirFilter: 'open' | 'using' | 'closed' = null

    let grid: GridClass = new GridClass()

    onMount(async () => {
        await loadMovements()
    })

    function actionBadge(action: Action): GridBadge {
        if (action === 'Abrindo') return new GridBadge({ label: 'Abrindo', color: '#00acc1' })
        if (action === 'Finalizando') return new GridBadge({ label: 'Finalizando', color: '#8e24aa' })
        return new GridBadge({ label: 'Usando', color: '#00897b' })
    }

    async function loadMovements() {
        try {
            show = true
            const from = dates?.from
            const to = dates?.to
            const serviceId = selectedService?.serviceId || undefined

            const [units, logs, discreteConsumptions] = await Promise.all([
                window.api.pos.stock.productConsumption.getAll({ from, to, serviceId }),
                window.api.pos.stock.productConsumption.findLogs({ from, to, serviceId }),
                window.api.pos.stock.productMovements.getAll({ from, to, serviceId, type: 'out_consumption' }),
            ])

            // sem filtro de serviço: units/logs não vêm com .service hidratado (productMovements já vem,
            // via include do backend) — resolve os nomes com uma única leitura de todos os insumos.
            let serviceMap: Record<string, Service> = {}
            if (selectedService) {
                serviceMap[selectedService.serviceId] = selectedService
            } else {
                const services = await window.api.pos.services.getAll({ type: 'consumable', excluded: true })
                for (const s of services as Service[]) serviceMap[s.serviceId] = s
            }

            let rows: Row[] = []
            for (const u of units as ProductOpenUnit[]) {
                const name = serviceMap[u.serviceId]?.name || '-'
                if (u.openedAt) {
                    rows.push({
                        name,
                        action: 'Abrindo',
                        when: u.openedAt,
                        detail: u.nominalVolume + ' ' + (u.volumeUnit || ''),
                        userId: u.openedByUserId,
                    })
                }
                if (u.closedAt) {
                    const declared = u.nominalVolume - (u.variance || 0)
                    rows.push({
                        name,
                        action: 'Finalizando',
                        when: u.closedAt,
                        detail: declared + ' ' + (u.volumeUnit || ''),
                        userId: u.closedByUserId,
                    })
                }
            }
            for (const l of logs as ProductConsumptionLog[]) {
                rows.push({
                    name: serviceMap[l.serviceId]?.name || '-',
                    action: 'Usando',
                    when: l.movedAt,
                    detail: l.quantity + ' ' + (serviceMap[l.serviceId]?.volumeUnit || ''),
                    userId: l.userId,
                    loggedUserId: l.loggedUserId,
                })
            }
            for (const m of discreteConsumptions as ProductMovement[]) {
                rows.push({
                    name: m.service?.name || serviceMap[m.serviceId]?.name || '-',
                    action: 'Usando',
                    when: m.movedAt,
                    detail: m.quantity + ' un',
                    userId: m.userId,
                    loggedUserId: m.loggedUserId,
                })
            }

            if (exibirFilter === 'open') rows = rows.filter((el) => el.action === 'Abrindo')
            if (exibirFilter === 'using') rows = rows.filter((el) => el.action === 'Usando')
            if (exibirFilter === 'closed') rows = rows.filter((el) => el.action === 'Finalizando')

            rows.sort((a, b) => new Date(b.when).getTime() - new Date(a.when).getTime())

            // atendente (userId) e usuário logado (loggedUserId): nem todo registro tem os dois — abertura/
            // finalização de frasco só guardam o atendente (ProductOpenUnit não rastreia quem estava logado).
            rows = await window.api.utils.utils.assignUsersFromObject(rows, false, 'userId', 'user', true)
            rows = await window.api.utils.utils.assignUsersFromObject(rows, false, 'loggedUserId', 'loggedUser', true)

            loadGrid(rows)
        } catch (error) {
            window.error(error)
        } finally {
            show = false
        }
    }

    function loadGrid(rows: Row[]) {
        const _grid = new GridClass()
        _grid.title = 'Movimentação de insumos'
        _grid.subtitle = 'Lançamentos de abertura, uso e finalização de insumos feitos pela agenda, mais recentes primeiro.'
        _grid.cols.push(new GridCol({ content: 'Insumo' }))
        _grid.cols.push(new GridCol({ content: 'Usuário', size: 2 }))
        _grid.cols.push(new GridCol({ content: 'Atendente', size: 2 }))
        _grid.cols.push(new GridCol({ content: 'Ação', type: GridColTypeEnum.badge, size: 2 }))
        _grid.cols.push(new GridCol({ content: 'Quando', type: GridColTypeEnum.datetime_friendly, size: 3 }))
        _grid.cols.push(new GridCol({ content: 'Detalhe', size: 2 }))
        for (const row of rows) {
            const line = new GridLine()
            line.slots.push({ content: row.name })
            line.slots.push({ content: row.loggedUser?.userName || '-' })
            line.slots.push({ content: row.user?.userName || '-' })
            line.slots.push({ content: actionBadge(row.action) })
            line.slots.push({ content: row.when })
            line.slots.push({ content: row.detail })
            _grid.lines.push(line)
        }
        grid = _grid
    }

    async function dateChanged(event) {
        dates = event.detail
        await loadMovements()
    }

    async function selectedServiceChanged() {
        await loadMovements()
    }

    async function exibirChanged() {
        await loadMovements()
    }
</script>

<Content footer={true}>
    <Zact>
        <div>
            <ZentitySelect fonte="servico" label="Insumo" params={{ type: 'consumable' }} bind:value={selectedService} on:change={selectedServiceChanged} />
        </div>
        <div>
            <Zinput type="select" label="Exibir" options={exibirOptions} bind:value={exibirFilter} on:change={exibirChanged} />
        </div>
        <DateSelect bind:this={dateSelect} startFrom={dates.from} startTo={dates.to} on:change={dateChanged} />
        <div class="spacer"></div>
    </Zact>
    <Zgrid class="h-space" {grid} />
    <LoadSpinner {show} />
</Content>
